'use client'
import { ComponentType, FC, PropsWithChildren } from 'react'
import { AppLayout, PrintedTicket, TicketActionButtons, TicketTerms } from '@/root/modules/tuuleliinid'
import { BookingContainer, InfoMessageContainer } from '@/root/components/tuuleliinid'
import { useConfig } from '@/root/shared-hooks/config/useConfig'
import { useCustomRouter } from '@/root/shared-hooks/useCustomRouter/useCustomRouter'
import { useGetTranslations } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { useTicketPage } from '@/root/modules/shared/TicketPage/hooks/useTicketPage'
import type { TicketPageView } from '@/root/modules/shared/TicketPage/helpers/getTicketPageView'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { TicketPageProps } from '@/app/[locale]/(routes)/ticket/[reservationToken]/ticket-page-shared'
import { TitleText } from '@/root/modules/tuuleliinid/TicketPage/TitleText'

const TicketHeader: FC<{ view: TicketPageView; reservationId: number }> = ({ view, reservationId }) => {
  const { getTranslation } = useGetTranslations()
  const { supportPhone } = useConfig()

  const ticketNumberTitle = (
    <TitleText>
      {getTranslation('ticketPrintedTicketNumber')} {reservationId}
    </TitleText>
  )

  switch (view.kind) {
    case 'cancelledByCustomer':
      return (
        <TitleText className="my-8">
          {getTranslation('ticketInfoCancelledBooking')}: {reservationId}
        </TitleText>
      )
    case 'bookedCancelledByAdmin':
      return (
        <>
          <Banner
            className="my-8"
            body={getTranslation('bookedTicketReservationSailCancelled', { phone: supportPhone })}
            variant="error"
          />
          {ticketNumberTitle}
        </>
      )
    case 'cancelledByAdmin':
      return (
        <>
          <Banner className="my-8" body={getTranslation('ticketReservationSailCancelled')} variant="error" />
          {ticketNumberTitle}
        </>
      )
    case 'editExpired':
      return (
        <>
          <Banner
            className="my-8"
            body={getTranslation('ticketImpossibleEditCancel', { days: view.days })}
            variant="error"
          />
          {ticketNumberTitle}
        </>
      )
    case 'booked':
      return (
        <>
          <Banner
            className="my-8"
            title={getTranslation('ticketBannerTitle')}
            body={getTranslation('ticketReservationBooked')}
            variant="warning"
          />
          {ticketNumberTitle}
        </>
      )
    case 'success':
      return (
        <>
          <Banner
            className="my-8"
            title={getTranslation('ticketBannerTitle')}
            body={getTranslation('ticketBannerBody')}
            variant="success"
          />
          <TitleText>{getTranslation('ticketConfirmationSuccessfulBooking')}</TitleText>
        </>
      )
    default:
      return null
  }
}

type TuuleliinidTicketPageProps = TicketPageProps & {
  /** The page frame. The TL header and footer by default; the trial theme passes its unbranded one. */
  Layout?: ComponentType<PropsWithChildren>
}

export const TicketPage: FC<TuuleliinidTicketPageProps> = ({ reservation: staticReservation, Layout = AppLayout }) => {
  const { push, locale } = useCustomRouter()
  const { getTranslation } = useGetTranslations()
  const ticketPage = useTicketPage(staticReservation)

  if (!ticketPage) return null

  const { ticketData, view, actions, hideQrCode } = ticketPage

  switch (view.kind) {
    case 'notFound':
      return (
        <Layout>
          <div className="flex justify-center rounded-sm md:my-10 md:p-8">
            <InfoMessageContainer
              body={getTranslation('ticketReservationNotFoundError')}
              ButtonText={getTranslation('ticketReservationNotFoundButton')}
              onClick={() => push(`/${locale}`)}
            />
          </div>
        </Layout>
      )
    case 'paymentPending':
      return (
        <Layout>
          <div className="flex justify-center rounded-sm md:my-10 md:p-8">
            <InfoMessageContainer
              title={getTranslation('paymentPendingHeading')}
              body={getTranslation('paymentPendingText')}
              ButtonText={getTranslation('paymentPendingReloadButton')}
              onClick={() => {}}
            />
          </div>
        </Layout>
      )
    case 'success':
    case 'booked':
    case 'cancelledByAdmin':
    case 'bookedCancelledByAdmin':
    case 'editExpired':
    case 'cancelledByCustomer':
      return (
        <Layout>
          <BookingContainer>
            <TicketHeader view={view} reservationId={ticketData.reservationId} />
            {actions && (
              <TicketActionButtons
                className="my-7"
                reservationId={actions.reservationId}
                reservationToken={actions.reservationToken}
                allowance={{ edit: actions.canEdit, cancel: actions.canCancel }}
              />
            )}
            <PrintedTicket ticketData={ticketData} hideQrCode={hideQrCode} />
            <TicketTerms />
          </BookingContainer>
        </Layout>
      )
  }
}

export default TicketPage
